import React, { useState } from 'react';
import { Tabs } from '../../../../Components/Tabs/Tabs';
import { DateRangePicker } from '../../../../Components/DateRangePicker';
import { LeaderboardTable } from '../LeaderboardTable/LeaderboardTable';
import { ModalLoader } from '../../../../Components/ModalLoader';
import {
  useLeaderboardData,
  useLeaderboardCurrentData,
  useLeaderboardColumns,
  useLeaderboardPagination,
} from './hooks';
import { useActiveLeaderboardEvent } from '../../../../hooks/useActiveLeaderboardEvent';
import './LeaderboardContent.scss';

type LeaderboardTab = 'participants' | 'areas' | 'offices';

const LEADERBOARD_TABS = [
  { id: 'participants' as LeaderboardTab, label: 'Participants' },
  { id: 'areas' as LeaderboardTab, label: 'Areas' },
  { id: 'offices' as LeaderboardTab, label: 'Offices' },
];

export const LeaderboardContent: React.FC = () => {
  const [activeTab, setActiveTab] = useState<LeaderboardTab>('participants');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const { activeEvent } = useActiveLeaderboardEvent();

  const {
    participantRows,
    areaRows,
    officeRows,
    myParticipant,
    participants,
    pageSize,
    participantsTotalPages,
    participantsPage,
    setParticipantsPage,
    districtsTotalPages,
    districtsPage,
    setDistrictsPage,
    officesTotalPages,
    officesPage,
    setOfficesPage,
    isLoading,
  } = useLeaderboardData(activeEvent?.id, startDate, endDate);

  const currentData = useLeaderboardCurrentData(
    activeTab,
    participantRows,
    areaRows,
    officeRows,
    participantsPage,
    myParticipant,
    participants,
    pageSize
  );

  const columns = useLeaderboardColumns(activeTab);

  const { totalPages, currentPage } = useLeaderboardPagination(
    activeTab,
    participantsTotalPages,
    participantsPage,
    districtsTotalPages,
    districtsPage,
    officesTotalPages,
    officesPage
  );

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId as LeaderboardTab);
    setParticipantsPage(1);
    setDistrictsPage(1);
    setOfficesPage(1);
  };

  const handlePageChange = (page: number) => {
    if (activeTab === 'participants') setParticipantsPage(page);
    else if (activeTab === 'areas') setDistrictsPage(page);
    else setOfficesPage(page);
  };

  const handleClearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setParticipantsPage(1);
    setDistrictsPage(1);
    setOfficesPage(1);
  };

  const isAnyFilterActive = Boolean(startDate || endDate);

  return (
    <div className="leaderboard-content">
      <div className="leaderboard-header-controls">
        <Tabs<LeaderboardTab>
          tabs={LEADERBOARD_TABS}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
        <div className={isAnyFilterActive ? 'filter-group filtered' : 'filter-group'}>
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            onClearFilters={handleClearFilters}
            minDate={activeEvent?.startDate ? new Date(activeEvent.startDate) : undefined}
            maxDate={activeEvent?.endDate ? new Date(activeEvent.endDate) : undefined}
            showClearButton={true}
          />
        </div>
      </div>

      <div className="leaderboard-table">
        {isLoading ? (
          <ModalLoader />
        ) : (
          <LeaderboardTable
            columns={columns}
            data={currentData}
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            getRowClassName={(row) => {
              if (activeTab !== 'participants') return '';
              const match = Boolean(myParticipant && row.id === myParticipant.id);
              return match ? 'your-position' : '';
            }}
            emptyMessage={
              activeTab === 'participants'
                ? 'No participants found'
                : activeTab === 'areas'
                  ? 'No areas found'
                  : 'No offices found'
            }
          />
        )}
      </div>
    </div>
  );
};
